import React, { useState } from 'react';
import axios from 'axios';
import './FindNearestUser.css';
import { MdSearch, MdLocationPin, MdOutlineHealthAndSafety } from 'react-icons/md';
import { FaUser, FaUserTag, FaCircle, FaPhoneAlt, FaMapMarkerAlt } from 'react-icons/fa';
import { ClipLoader } from 'react-spinners';
import { motion } from 'framer-motion';

const FindNearestUser = () => {
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [nearestUsers, setNearestUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleFindNearestUser = async () => {
    setLoading(true);
    setNearestUsers([]);
    setError('');

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            setLatitude(position.coords.latitude);
            setLongitude(position.coords.longitude);

            const response = await axios.get("https://api.swasthmitra.com/api/usermedical/find-nearest", {
              params: {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
              },
            });

            // Filter for Swasth Mitra (employees) with approved status
            const swasthMitraUsers = response.data.users.filter(user => 
              user.role === "employee" && user.status === "approved"
            );
            
            setNearestUsers(swasthMitraUsers.slice(0, 8));
            
            // Show message if no Swasth Mitra found
            if (swasthMitraUsers.length === 0) {
              setError('No Swasth Mitra found in your area. Please try again later.');
            }
          } catch (error) {
            console.error("Error finding nearest Swasth Mitra", error);
            setError('Failed to find Swasth Mitra. Please try again.');
          } finally {
            setLoading(false);
          }
        },
        (error) => {
          setLoading(false);
          setError('Unable to access your location. Please enable location services and try again.');
        }
      );
    } else {
      setLoading(false);
      setError('Geolocation is not supported by your browser.');
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'approved':
        return '#10b981';
      case 'pending':
        return '#f59e0b';
      case 'rejected':
        return '#ef4444';
      default:
        return '#94a3b8';
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="nearest-user-container">
      {/* Floating health icons background */}
      <div className="floating-icons">
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={i}
            className="floating-icon"
            initial={{ y: 0, x: Math.random() * 100 }}
            animate={{
              y: [0, -50, 0],
              x: [Math.random() * 50, Math.random() * 50]
            }}
            transition={{
              duration: 15 + Math.random() * 10,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <MdOutlineHealthAndSafety />
          </motion.div>
        ))}
      </div>

      <div className="nearest-user-content">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="header-section"
        >
          <div className="title-decoration">
            <div className="decoration-line"></div>
            <div className="decoration-dot"></div>
          </div>
          
          <h1 className="main-title">
            <span className="title-gradient">Find Your Nearest</span>
            <span className="title-accent">Swasth Mitra</span>
          </h1>
          
          <p className="subtitle">
            Discover experienced health professionals ready to assist you in your area
          </p>
          
          <motion.button
            whileHover={{ 
              scale: 1.05,
              boxShadow: "0 8px 25px rgba(128, 0, 128, 0.4)"
            }}
            whileTap={{ scale: 0.98 }}
            onClick={handleFindNearestUser}
            className="search-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <ClipLoader size={18} color={"#ffffff"} /> 
                <span>Locating...</span>
              </>
            ) : (
              <>
                <MdSearch size={26} /> 
                <span>Swasth mitra near you</span>
              </>
            )}
          </motion.button>
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
        </motion.div>

        {nearestUsers.length > 0 && (
          <div className="results-section">
            <motion.h2 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="results-title"
            >
              <span>Available Guides Near You</span>
              <div className="title-underline"></div>
            </motion.h2>
            
            <motion.div 
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="user-cards-container"
            >
              <div className="user-cards-scroll">
                {nearestUsers.map((user, index) => (
                  <motion.div
                    key={user._id}
                    variants={itemVariants}
                    className="user-card"
                    whileHover={{ 
                      y: -5,
                      boxShadow: "0 15px 30px rgba(0, 0, 0, 0.12)"
                    }}
                  >
                    <div className="card-decoration"></div>
                    <div className="user-avatar">
                      <img
                        src={user.imageUrl || 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png'}
                        alt={user.name}
                        onError={(e) => {
                          e.target.src = 'https://cdn-icons-png.flaticon.com/512/3135/3135715.png';
                        }}
                      />
                      <div 
                        className="status-badge"
                        style={{ backgroundColor: getStatusColor(user.status) }}
                      />
                    </div>
                    
                    <div className="user-info">
                      <h3>{user.name}</h3>
                      <p className="user-role">
                        <FaUserTag /> Swasth Mitra
                      </p>
                      
                      <div className="user-contact">
                        <a href={`tel:${user.MobileNumber}`} className="contact-link">
                          <FaPhoneAlt /> {user.MobileNumber}
                        </a>
                      </div>
                      
                      <div className="user-meta">
                        <span className="status">
                          <FaCircle size={10} color={getStatusColor(user.status)} /> 
                          {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                        </span>
                        <span className="location">
                          <FaMapMarkerAlt /> Nearby
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FindNearestUser;